import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { API } from 'api';
import {
  ExternalFeatureLayer,
  ExternalFeatureLayerServer,
} from 'features/mapping/mapping.types';
import { getExternalFeatureLayerId } from 'features/mapping/mapping.utils';
import { useAuthState } from 'state/useAuthState';
import { LayerProps } from 'shared/map-exports';
import { useInvalidateQueryOnAuthChange } from 'hooks/useInvalidateQueryOnAuthChange';
import { kebabCaseKeys } from './kebabCaseKeys';

type UseExternalFeatureLayers = Omit<
  UseQueryResult<ExternalFeatureLayer[], Error>,
  'data'
> & {
  externalFeatureLayers: ExternalFeatureLayer[];
};

/**
 * Since our API (`src/api.ts`) rewrites all JSON keys to camelCase, but the mapbox-gl library
 * expects style instructions to be in kebab-case, we need to revert the keys back to kebab-case
 * for the `displayStyleData` of external feature layers that use embedded styles.
 */
const maybeRevertStyleDataToKebabCase = (
  data: ExternalFeatureLayerServer,
): ExternalFeatureLayerServer => {
  if (
    data.displayStyleData &&
    data.displayStyleData.displayStyleType === 'embedded'
  ) {
    return {
      ...data,
      displayStyleData: {
        ...data.displayStyleData,
        layers: data.displayStyleData.layers.map(
          (layer) => kebabCaseKeys(layer) as LayerProps,
        ),
      },
    };
  }
  return data;
};

export const parseExternalFeatureLayerServer = (
  layers: ExternalFeatureLayerServer[],
): ExternalFeatureLayer[] =>
  layers.map((layer) => ({
    ...maybeRevertStyleDataToKebabCase(layer),
    dateCreated: new Date(layer.dateCreated),
    dateModified: new Date(layer.dateModified),
    mapLayerId: getExternalFeatureLayerId(layer),
    includeInLayerList: layer.includeInLayerList,
    includeInLayerListForAdmins: layer.includeInLayerListForAdmins,
  }));

const fetchExternalFeatureLayers = async (): Promise<
  ExternalFeatureLayer[]
> => {
  const result = await API.get<ExternalFeatureLayerServer[]>(
    'feature_layer_mirrors/feature_layers/',
  );
  return parseExternalFeatureLayerServer(result.data);
};

const queryKey = ['featureLayerMirrors'];

export const useExternalFeatureLayers = (): UseExternalFeatureLayers => {
  const { isOrganizationUser } = useAuthState();
  useInvalidateQueryOnAuthChange(queryKey);

  const { data = [], ...results } = useQuery({
    queryKey,
    queryFn: async () => fetchExternalFeatureLayers(),
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: isOrganizationUser,
  });

  const externalFeatureLayers = data.filter(
    (featLayer) => featLayer.mapLayerId,
  ) as ExternalFeatureLayer[];

  return {
    ...results,
    externalFeatureLayers,
  };
};
